/// <reference types="vite/client" />
/**
 * Application Providers Tree
 * Clean composition of core infrastructure, data, theme, and authentication providers.
 */

import React, { ReactNode } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient } from '../../lib/queryClient';
import { ThemeProvider } from '../../context/ThemeContext';
import { AuthProvider } from '../../context/AuthContext';
import { LayoutProvider } from '../../context/LayoutContext';
import { UIProvider } from '../../context/UIContext';
import { ErrorProvider } from '../../context/ErrorContext';
import { ErrorBoundary } from '../../shared/error/ErrorBoundary';
import { RepositoryProvider } from '../../core/di/RepositoryContext';
import { SubscriptionProvider } from '../../features/subscription/context/SubscriptionContext';

interface AppProvidersProps {
  children: ReactNode;
}

export const AppProviders: React.FC<AppProvidersProps> = ({ children }) => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <SubscriptionProvider>
            <LayoutProvider>
              <UIProvider>
                <ErrorProvider>
                  <ErrorBoundary>
                    <RepositoryProvider>
                      {children}
                    </RepositoryProvider>
                  </ErrorBoundary>
                </ErrorProvider>
              </UIProvider>
            </LayoutProvider>
          </SubscriptionProvider>
        </AuthProvider>
      </ThemeProvider>

      {import.meta.env.DEV && (
        <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-right" />
      )}
    </QueryClientProvider>
  );
};
